import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById } from '@/lib/api/processors'
import { useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { TagadaPayEditSkeleton } from '../components/tagadapay-edit-skeleton'
import { ProcessorApmSettings, ApmSettings, initApmSettingsFromOptions, getMorPublishableKey } from '../../components/processor-apm-settings'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { MOR_PUBLISHABLE_KEYS, isTestEnvironment } from '@/lib/payment-constants'

const TAGADAPAY_RADAR_TEST_KEY = MOR_PUBLISHABLE_KEYS.tagadapay.test
const TAGADAPAY_RADAR_PROD_KEY = MOR_PUBLISHABLE_KEYS.tagadapay.live

const TagadaPayEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id!)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [apmSettings, setApmSettings] = useState<ApmSettings>({})
    const [expressPublishableKey, setExpressPublishableKey] = useState<string>('')
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        accountId: '',
        descriptorSuffix: '',
        radarEnabled: false,
        radarPublishableKey: '',
        dryMode: false,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            const descriptor = options?.descriptor as { descriptor_suffix?: string } | undefined
            const radar = options?.radar as { enabled?: boolean; publishableKey?: string } | undefined

            // Auto-fill radar key if enabled but not set
            const radarEnabled = radar?.enabled ?? false
            let radarPublishableKey = radar?.publishableKey || ''
            if (radarEnabled && !radarPublishableKey) {
                radarPublishableKey = isTestEnvironment() ? TAGADAPAY_RADAR_TEST_KEY : TAGADAPAY_RADAR_PROD_KEY
            }

            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                accountId: options?.accountId || '',
                descriptorSuffix: descriptor?.descriptor_suffix || '',
                radarEnabled,
                radarPublishableKey,
                dryMode: options?.dryMode === true,
            })
            setApmSettings(initApmSettingsFromOptions('tagadapay', options?.apmSettings as Record<string, Record<string, unknown>> | undefined))
            setExpressPublishableKey(options?.expressPublishableKey || getMorPublishableKey('tagadapay'))
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (!formData.accountId.trim()) {
            setValidationError(t('tagadapay.configuration.accountIdRequired'))
            return
        }

        // Mirrors the server guard: only a live processor is held to the shape, so
        // an existing misconfigured one can still be saved as disabled.
        if (formData.enabled && !formData.accountId.trim().startsWith('acct_')) {
            setValidationError(t('tagadapay.configuration.accountIdInvalidFormat'))
            return
        }

        if (!formData.descriptorSuffix.trim()) {
            setValidationError(t('tagadapay.descriptor.descriptorSuffixRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    type: processor!.type,
                    name: formData.name,
                    enabled: formData.enabled,
                    options: {
                        accountId: formData.accountId,
                        descriptor: {
                            descriptor_suffix: formData.descriptorSuffix,
                        },
                        radar: {
                            enabled: formData.radarEnabled,
                            publishableKey: formData.radarPublishableKey,
                        },
                        apmSettings,
                        expressPublishableKey: expressPublishableKey || undefined,
                        dryMode: formData.dryMode,
                        // Ops-set, no UI: preserve across saves (options is replaced wholesale).
                        suppressWalletReceiptEmail:
                            (processor!.options as any)?.suppressWalletReceiptEmail || undefined,
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(-1)
    }

    if (isLoading) {
        return (
            <div className="container mx-auto py-8">
                <TagadaPayEditSkeleton />
            </div>
        )
    }

    if (!processor || processor.type !== 'tagadapay') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('tagadapay.editTitle')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <Tabs defaultValue="configuration">
                <TabsList className="mb-6">
                    <TabsTrigger value="configuration">{t('configuration')}</TabsTrigger>
                    <TabsTrigger value="payment-methods">{t('paymentMethods')}</TabsTrigger>
                </TabsList>

                <TabsContent value="configuration">
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('tagadapay.configuration.tagadaPayConfigurationTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="accountId">{t('tagadapay.configuration.accountIdLabel')} *</Label>
                            <Input
                                id="accountId"
                                value={formData.accountId}
                                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                placeholder={t('tagadapay.configuration.accountIdPlaceholder')}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('tagadapay.configuration.accountIdDescription')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('statementDescriptor')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="descriptorSuffix">{t('tagadapay.descriptor.descriptorSuffixLabel')} *</Label>
                            <Input
                                id="descriptorSuffix"
                                value={formData.descriptorSuffix}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase()
                                    setFormData({ ...formData, descriptorSuffix: value })
                                }}
                                maxLength={12}
                                placeholder={t('tagadapay.descriptor.descriptorSuffixPlaceholder')}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('tagadapay.descriptor.descriptorSuffixDescription')}
                            </p>
                        </div>

                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('tagadapay.descriptor.exampleStatement')}:</strong> TAGADAPAY*{formData.descriptorSuffix || 'SUFFIX'}
                            </p>
                            <p className="text-xs text-blue-600 mt-2">
                                {t('tagadapay.descriptor.descriptorMaxLength')}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">
                                <strong>{t('tagadapay.requirements.requirementsForSuffix')}:</strong>
                            </p>
                            <ul className="list-disc pl-4 text-sm text-muted-foreground space-y-1">
                                <li>{t('tagadapay.requirements.requirement1')}</li>
                                <li>{t('tagadapay.requirements.requirement2')}</li>
                                <li>{t('tagadapay.requirements.requirement3')}</li>
                                <li>{t('tagadapay.requirements.requirement4')}</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('tagadapay.radar.radarFraudProtectionTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="radarEnabled"
                                checked={formData.radarEnabled}
                                onCheckedChange={(checked) => {
                                    const isEnabled = checked === true
                                    const publishableKey = isEnabled
                                        ? isTestEnvironment()
                                            ? TAGADAPAY_RADAR_TEST_KEY
                                            : TAGADAPAY_RADAR_PROD_KEY
                                        : formData.radarPublishableKey

                                    setFormData({
                                        ...formData,
                                        radarEnabled: isEnabled,
                                        radarPublishableKey: publishableKey,
                                    })
                                }}
                            />
                            <Label htmlFor="radarEnabled">{t('tagadapay.radar.enableRadarLabel')}</Label>
                        </div>

                        {formData.radarEnabled && (
                            <div className="space-y-4">
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                    <p className="text-sm text-blue-700">
                                        {t('tagadapay.radar.radarEnabledDescription')}
                                    </p>
                                </div>

                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                    <p className="text-sm font-medium text-blue-700">
                                        {t('tagadapay.radar.radarFeatures')}
                                    </p>
                                    <ul className="mt-1 list-disc pl-4 text-xs text-blue-700">
                                        <li>{t('tagadapay.radar.feature1')}</li>
                                        <li>{t('tagadapay.radar.feature2')}</li>
                                        <li>{t('tagadapay.radar.feature3')}</li>
                                        <li>{t('tagadapay.radar.feature4')}</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
                </TabsContent>

                <TabsContent value="payment-methods">
                    <ProcessorApmSettings
                        processorType="tagadapay"
                        value={apmSettings}
                        onChange={setApmSettings}
                        expressPublishableKey={expressPublishableKey}
                        disabled={updateProcessorMutation.isPending}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}

export const TagadaPayEditPage = withRequiredPermission(TagadaPayEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)

